import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CustomerModal from "../components/CustomerModal";
import ConfirmDialog from "../components/ConfirmDialog";
import TopReport from "../components/TopReport";
import { useFillHeight } from "../lib/useFillHeight";
import CollapseBar, { BarTitle } from "../components/CollapseBar";
import InlineCell from "../components/InlineCell";
import NotesModal from "../components/NotesModal";
import HesaplamaModal from "../components/HesaplamaModal";
import ScanCardsModal from "../components/ScanCardsModal";
import AiSitesModal from "../components/AiSitesModal";
import { Sparkles } from "lucide-react";
import WhatsAppModal from "../components/WhatsAppModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Briefcase, X, Plus, Search, Download, Upload, FileSpreadsheet, Trash2, Pencil, Filter, Archive, MessageSquare, Calculator, ScanLine, MessageCircle, Save, AlertTriangle, ChevronDown, ChevronRight, Copy,
  Share2,
} from "lucide-react";
import CopyToFirmsDialog from "../components/CopyToFirmsDialog";
import FillInfoModal from "../components/FillInfoModal";
import CopyLog from "../components/CopyLog";
import DeleteAllCustomers from "../components/DeleteAllCustomers";
import { History } from "lucide-react";
import { toast } from "sonner";
import { TOKEN_KEY, formatTRY, num } from "../lib/mw";

const HESAP_KEYS = ["hesap", "teklif", "teklif_fiyat", "il_cirolari", "il_ciro", "cikis_il_cirolari", "rut", "koli_palet"];
const hasHesap = (row) => {
  const d = row.data || {};
  return HESAP_KEYS.some((k) => { const v = d[k]; return v !== undefined && v !== null && String(v).trim() !== ""; });
};

const MT = { "": "TEL/ADRES", tel: "TELEFON", adres: "ADRES", vkn: "VERGİ NO" };
const blank = (v) => !String(v || "").trim();
const isMissing = (r, t) => {
  const d = r.data || {};
  const tel = blank(d.gsm) && blank(d.s_tel), adr = blank(d.adres), vkn = blank(d.vergi_no) && blank(d.vkn);
  return t === "tel" ? tel : t === "adres" ? adr : t === "vkn" ? vkn : tel || adr;
};

export default function Customers({ room = false }) {
  const { columns, isAdmin, isSuperAdmin, loadColumns, perms } = useAuth();
  const allow = (k) => (perms ? !!perms[k] : false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [ilFilter, setIlFilter] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [canCopy, setCanCopy] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [fillQ, setFillQ] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  useEffect(() => { api.get("/customers-copy/targets", { silent: true }).then(() => setCanCopy(true)).catch(() => setCanCopy(false)); }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, ...}
  const [localCols, setLocalCols] = useState([]);
  const [dragKey, setDragKey] = useState(null);
  const [reportKey, setReportKey] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [colFilters, setColFilters] = useState({});
  const [sortBy, setSortBy] = useState({ key: "", dir: "asc" });
  const [reportOpen, setReportOpen] = useState(false);
  const [repSube, setRepSube] = useState("");
  const [repGroup, setRepGroup] = useState("il");
  const [repCols, setRepCols] = useState(["firma", "il", "hedef_ciro", "gerceklesen_ciro"]);
  const [hesapOnly, setHesapOnly] = useState(false);
  const [eksikOnly, setEksikOnly] = useState(false);
  const [missingTotal, setMissingTotal] = useState(null);
  const [mtype, setMtype] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [dirty, setDirty] = useState({});
  const [fillKeys, setFillKeys] = useState([]);
  const [savingAll, setSavingAll] = useState(false);
  const dirtyRef = useRef(false);
  const dirtyCount = Object.keys(dirty).length;

  // debounce search (no per-keystroke backend hit)
  useEffect(() => { const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 350); return () => clearTimeout(t); }, [searchInput]);
  useEffect(() => { dirtyRef.current = dirtyCount > 0; }, [dirtyCount]);
  useEffect(() => {
    const h = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  const toggleSort = (k) => setSortBy((s) => s.key !== k ? { key: k, dir: "asc" } : s.dir === "asc" ? { key: k, dir: "desc" } : { key: "", dir: "asc" });
  const viewRows = useMemo(() => {
    let out = rows;
    if (hesapOnly) out = out.filter(hasHesap);
    if (eksikOnly) out = out.filter((r) => isMissing(r, mtype));
    const active = Object.entries(colFilters).filter(([, v]) => v && v.trim());
    if (active.length) out = out.filter((row) => active.every(([k, v]) => String(row.data[k] ?? "").toLocaleUpperCase("tr-TR").includes(v.toLocaleUpperCase("tr-TR"))));
    if (sortBy.key) {
      const dir = sortBy.dir === "desc" ? -1 : 1;
      out = [...out].sort((a, b) => {
        const av = a.data[sortBy.key] ?? "", bv = b.data[sortBy.key] ?? "";
        const bothNum = String(av).trim() !== "" && String(bv).trim() !== "" && !isNaN(parseFloat(av)) && !isNaN(parseFloat(bv));
        if (bothNum) return (num(av) - num(bv)) * dir;
        return String(av).localeCompare(String(bv), "tr") * dir;
      });
    }
    return out;
  }, [rows, colFilters, sortBy, hesapOnly, eksikOnly, mtype]);
  const eksikCount = useMemo(() => rows.filter((r) => isMissing(r, mtype)).length, [rows, mtype]);
  const subeler = useMemo(() => [...new Set(rows.map((r) => r.data.subesi).filter(Boolean))], [rows]);
  const numCols = useMemo(() => columns.filter((c) => c.type === "currency" || c.type === "number"), [columns]);
  const repData = useMemo(() => {
    const list = repSube ? rows.filter((r) => r.data.subesi === repSube) : rows;
    const gk = repGroup || "il";
    const groups = {};
    list.forEach((r) => {
      const gv = r.data[gk] || "—";
      const g = groups[gv] || (groups[gv] = { key: gv, count: 0, sums: {} });
      g.count++;
      numCols.forEach((c) => { g.sums[c.key] = (g.sums[c.key] || 0) + num(r.data[c.key]); });
    });
    const totals = {};
    numCols.forEach((c) => { totals[c.key] = list.reduce((a, r) => a + num(r.data[c.key]), 0); });
    return { list, groups: Object.values(groups).sort((a, b) => b.count - a.count), totals };
  }, [rows, repSube, repGroup, numCols]);
  const [notesFor, setNotesFor] = useState(null);
  const [hesapFor, setHesapFor] = useState(null);
  const [locked, setLocked] = useState(false);
  const [pendingReq, setPendingReq] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState(null);
  const [waFor, setWaFor] = useState(null);
  const [roomAll, setRoomAll] = useState([]);
  const [roomQ, setRoomQ] = useState("");
  const [roomRes, setRoomRes] = useState([]);
  const searchRoom = async (q) => {
    setRoomQ(q);
    if (!q || q.trim().length < 2) { setRoomRes([]); return; }
    try {
      const { data } = await api.get("/customers", { params: { search: q.trim(), page_size: 20 }, silent: true });
      const inRoom = new Set(roomAll.map((r) => r.id));
      setRoomRes((data.items || []).filter((c) => !inRoom.has(c.id)));
    } catch { setRoomRes([]); }
  };
  const addToRoom = async (c) => {
    try { await api.post("/workroom/add", { data: { ids: [c.id] } }); toast.success(`${c.data?.firma || "MÜŞTERİ"} ODAYA EKLENDİ`); setRoomQ(""); setRoomRes([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "EKLENEMEDİ"); }
  };
  const bulkToRoom = async () => {
    try { await api.post("/workroom/add", { data: { ids: [...selected] } }); toast.success(`${selected.size} MÜŞTERİ ODAYA EKLENDİ`); setSelected(new Set()); }
    catch (e) { toast.error(e.response?.data?.detail || "EKLENEMEDİ"); }
  };
  const bulkFromRoom = async () => {
    try { await api.post("/workroom/remove", { data: { ids: [...selected] } }); toast.success(`${selected.size} MÜŞTERİ ODADAN ÇIKARILDI`); load(); }
    catch (e) { toast.error("ÇIKARILAMADI"); }
  };
  const removeFromRoom = async (row) => {
    try { await api.post("/workroom/remove", { data: { ids: [row.id] } }); toast.success("ODADAN ÇIKARILDI"); load(); }
    catch (e) { toast.error("ÇIKARILAMADI"); }
  };

  const loadPending = useCallback(() => {
    api.get("/delete-requests", { silent: true }).then((r) => setPendingReq((r.data || []).find((x) => x.status === "pending") || null)).catch(() => {});
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  useEffect(() => { api.get("/geo/provinces", { silent: true }).then((r) => setProvinces(r.data)); }, []);
  useEffect(() => { setLocalCols(columns.filter((c) => c.visible)); }, [columns]);

  const load = useCallback(() => {
    if (dirtyRef.current && !window.confirm("KAYDEDİLMEMİŞ DEĞİŞİKLİKLER VAR. KAYDETMEDEN DEVAM EDERSENİZ KAYBOLUR. DEVAM?")) return;
    const ps = pageSize === "all" ? 0 : pageSize;
    if (room) {
      api.get("/workroom", { silent: true }).then((r) => {
        const all = r.data.items || [];
        const s = search.trim().toLocaleUpperCase("tr-TR");
        const f = all.filter((c) => !!c.archived === showArchived && (!ilFilter || (c.data.il || "") === ilFilter) && (!s || ["firma", "yetkili", "email", "gsm"].some((k) => String(c.data[k] || "").toLocaleUpperCase("tr-TR").includes(s))));
        setRoomAll(all);
        setRows(ps ? f.slice((page - 1) * ps, page * ps) : f);
        setTotal(f.length);
        setSelected(new Set());
        setDirty({});
      }).catch(() => {});
      return;
    }
    api.get("/customers", { params: { page, page_size: ps, search, il: ilFilter, archived: showArchived ? "true" : "false", missing: eksikOnly ? "true" : "", mtype }, silent: true }).then((r) => {
      setRows(r.data.items);
      setTotal(r.data.total);
      setMissingTotal(r.data.missing_total ?? null);
      setSelected(new Set());
      setDirty({});
    });
  }, [page, pageSize, search, ilFilter, showArchived, room, eksikOnly, mtype]);

  useEffect(() => { load(); }, [load]);

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(viewRows.map((r) => r.id)));
    else setSelected(new Set());
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleExpand = (row) => setDetailRow(row);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  // local edit buffer — NO network, NO overlay flash. Save with the Save button.
  const editCell = useCallback((row, patch) => {
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, data: { ...r.data, ...patch } } : r)));
    setDirty((d) => ({ ...d, [row.id]: true }));
  }, []);

  // Excel-like drag-to-fill
  const SPECIAL_CELLS = useMemo(() => new Set(["hesaplama", "il_cirolari", "cikis_il_cirolari", "teklif", "rut"]), []);
  const viewRowsRef = useRef([]);
  const editColsRef = useRef([]);
  useEffect(() => { viewRowsRef.current = viewRows; editColsRef.current = localCols.filter((c) => !SPECIAL_CELLS.has(c.key)).map((c) => c.key); });
  const fillSet = useMemo(() => new Set(fillKeys), [fillKeys]);
  const scrollRef = useRef(null);
  useFillHeight(scrollRef);
  const rowVirtualizer = useVirtualizer({ count: viewRows.length, getScrollElement: () => scrollRef.current, estimateSize: () => 24, overscan: 12, getItemKey: (i) => (viewRows[i] ? viewRows[i].id : i) });
  const applyFill = (keys, val) => {
    const vr = viewRowsRef.current;
    const byIdx = {};
    keys.forEach((k) => { const ci = k.indexOf(":"); const i = +k.slice(0, ci); (byIdx[i] = byIdx[i] || {})[k.slice(ci + 1)] = val; });
    const idPatch = {};
    Object.entries(byIdx).forEach(([i, patch]) => { const r = vr[+i]; if (r) idPatch[r.id] = patch; });
    setRows((rs) => rs.map((r) => (idPatch[r.id] ? { ...r, data: { ...r.data, ...idPatch[r.id] } } : r)));
    setDirty((d) => { const nd = { ...d }; Object.keys(idPatch).forEach((id) => { nd[id] = true; }); return nd; });
  };
  const startFill = useCallback((e, srcIdx, srcCol) => {
    e.preventDefault(); e.stopPropagation();
    const vr = viewRowsRef.current;
    const srcVal = vr[srcIdx]?.data?.[srcCol] ?? "";
    const cols = editColsRef.current;
    const srcPos = cols.indexOf(srcCol);
    const move = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = el && el.closest ? el.closest("[data-fillcell]") : null;
      if (!cell) return;
      const tIdx = +cell.getAttribute("data-rowidx");
      const tCol = cell.getAttribute("data-colkey");
      const keys = [];
      if (tCol === srcCol) {
        const a = Math.min(srcIdx, tIdx), b = Math.max(srcIdx, tIdx);
        for (let i = a; i <= b; i++) keys.push(i + ":" + srcCol);
      } else if (tIdx === srcIdx) {
        const tp = cols.indexOf(tCol); if (tp < 0) return;
        const a = Math.min(srcPos, tp), b = Math.max(srcPos, tp);
        for (let p = a; p <= b; p++) keys.push(srcIdx + ":" + cols[p]);
      } else { return; }
      setFillKeys(keys);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setFillKeys((keys) => { if (keys.length > 1) applyFill(keys, srcVal); return []; });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, []);

  const saveAll = async () => {
    const ids = Object.keys(dirty);
    if (!ids.length) { toast.info("KAYDEDİLECEK DEĞİŞİKLİK YOK"); return; }
    setSavingAll(true);
    try {
      for (const id of ids) {
        const row = rows.find((r) => String(r.id) === String(id));
        if (row) await api.put(`/customers/${row.id}`, { data: row.data, archived: row.archived }, { silent: true });
      }
      setDirty({});
      toast.success(`${ids.length} KAYIT KAYDEDİLDİ`);
      setReportKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "KAYIT HATASI"); }
    setSavingAll(false);
  };

  const archiveRow = async (row) => {
    await api.put(`/customers/${row.id}`, { data: row.data, archived: !row.archived });
    toast.success(row.archived ? "ARŞİVDEN ÇIKARILDI" : "ARŞİVLENDİ");
    load();
    setReportKey((k) => k + 1);
  };

  const updateNoteCount = (id, c) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, note_count: c } : r)));

  const doDelete = async () => {
    if (confirm.type === "single") {
      await api.delete(`/customers/${confirm.id}`);
      toast.success("MÜŞTERİ SİLİNDİ");
    } else if (confirm.type === "selected") {
      await api.post("/customers/bulk-delete", { data: { ids: [...selected] } });
      toast.success(`${selected.size} MÜŞTERİ SİLİNDİ`);
    }
    setConfirm(null);
    load();
    setReportKey((k) => k + 1);
  };

  const cancelPending = async () => {
    try {
      await api.post(`/delete-requests/${pendingReq.id}/cancel`);
      toast.success("VERİLER GERİ GETİRİLDİ");
      setPendingReq(null); load(); setReportKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "HATA"); }
  };

  const exportExcel = async () => {
    const info = await api.get("/tenant/info", { silent: true });
    if (!info.data.has_pin) { toast.error("ÖNCE AYARLAR > GÜVENLİK'TEN PIN TANIMLAYIN"); return; }
    const pin = window.prompt("EXCEL İNDİRME PİNİNİ GİRİN"); if (pin === null) return;
    const pass = window.prompt("ŞİFRENİZİ GİRİN (GÜVENLİK)"); if (pass === null) return;
    download("/customers/export", "musteriler.xlsx", pin, pass);
  };
  const template = () => download("/customers/template", "musteri_sablon.xlsx");

  const download = async (path, name, pin, pass) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = { Authorization: `Bearer ${token}` };
    const tid = localStorage.getItem("mwcrm_tenant"); if (tid) headers["X-Tenant-Id"] = tid;
    const yr = localStorage.getItem("mwcrm_year"); if (yr) headers["X-Year"] = yr;
    if (pin) headers["X-Export-Pin"] = pin;
    if (pass) headers["X-Export-Pass"] = pass;
    const res = await fetch(`${api.defaults.baseURL}${path}`, { headers });
    if (!res.ok) { toast.error("İNDİRME ENGELLENDİ (PIN VEYA ŞİFRE HATALI OLABİLİR)"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleLock = async () => {
    if (locked) {
      const info = await api.get("/tenant/info", { silent: true });
      if (info.data.has_pin) {
        const pin = window.prompt("GÖRÜNÜMÜ AÇMAK İÇİN PIN");
        if (pin === null) return;
        const v = await api.post("/tenant/verify-pin", { pin });
        if (!v.data.ok) { toast.error("PIN HATALI"); return; }
      }
      setLocked(false);
    } else setLocked(true);
  };

  const importExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} MÜŞTERİ YÜKLENDİ`);
      load();
      setReportKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "YÜKLEME HATASI");
    }
    e.target.value = "";
  };

  const copyText = (t) => { try { navigator.clipboard.writeText(t); toast.success("KOPYALANDI"); } catch (e) { toast.error("KOPYALANAMADI"); } };
  const plainText = (row) => localCols.map((c) => `${c.label}: ${row.data[c.key] ?? ""}`).join("\n");

  const [colW, setColW] = useState(() => { try { return JSON.parse(localStorage.getItem("mwcrm_colw_customers") || "{}"); } catch { return {}; } });
  const resizing = useRef(null);
  const startResize = (key, e) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.target.closest("th");
    const startX = e.clientX; const startW = th.offsetWidth;
    resizing.current = key;
    const move = (ev) => { const w = Math.max(60, startW + (ev.clientX - startX)); setColW((p) => ({ ...p, [key]: w })); };
    const up = () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      setColW((p) => { try { localStorage.setItem("mwcrm_colw_customers", JSON.stringify(p)); } catch (e2) {} return p; });
      setTimeout(() => { resizing.current = null; }, 60);
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  // column drag reorder (admin)
  const onDrop = async (targetKey) => {
    if (!isAdmin || !dragKey || dragKey === targetKey) return;
    const all = [...columns];
    const from = all.findIndex((c) => c.key === dragKey);
    const to = all.findIndex((c) => c.key === targetKey);
    const [moved] = all.splice(from, 1);
    all.splice(to, 0, moved);
    all.forEach((c, i) => (c.order = i + 1));
    await api.put("/settings/columns", { data: { columns: all } });
    await loadColumns();
    setDragKey(null);
    toast.success("KOLON SIRASI GÜNCELLENDİ");
  };

  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="mw-fade-up space-y-4">
      <BarTitle><div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {room && <Briefcase className="h-7 w-7 text-amber-400" />}
          <div>
            <h1 className="font-head text-3xl font-bold tracking-tight text-white">{room ? "ÇALIŞMA ODASI" : "MÜŞTERİLER"}</h1>
            <p className="text-sm text-slate-400">{total} KAYIT{room ? " — SEÇİLİ ÇALIŞMA LİSTESİ" : ""}</p>
          </div>
        </div>
        <div className="relative w-64 shrink-0">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="customer-quick-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="FİRMA ARA..." className="h-9 border-slate-700 bg-slate-800 pl-8 text-slate-100" />
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          {dirtyCount > 0 && (
            <Button data-testid="save-changes-button" onClick={saveAll} disabled={savingAll} className="bg-emerald-600 text-white hover:bg-emerald-700 animate-pulse">
              <Save className="mr-1 h-4 w-4" /> DEĞİŞİKLİKLERİ KAYDET ({dirtyCount})
            </Button>
          )}
          <CollapseBar id={room ? "room-actions" : "customers-actions"} label="İŞLEMLER" className="justify-end">
          {canCopy && (
            <Button data-testid="copy-log-button" variant="outline" onClick={() => setLogOpen(true)} className="shrink-0 border-sky-800 bg-sky-950/40 text-sky-300"><History className="mr-1 h-4 w-4" /> AKTARMA GEÇMİŞİ</Button>
          )}
          <Button data-testid="add-customer-button" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="mr-1 h-4 w-4" /> YENİ FİRMA EKLE
          </Button>
          {allow("can_export") && (
          <Button data-testid="excel-export-button" variant="outline" onClick={exportExcel} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">
            <Download className="mr-1 h-4 w-4" /> EXCEL İNDİR (PIN)
          </Button>
          )}
          {allow("can_import") && (
          <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700">
            <Upload className="mr-1 h-4 w-4" /> EXCEL YÜKLE
            <input data-testid="excel-import-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} />
          </label>
          )}
          <Button variant="outline" onClick={template} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">
            <FileSpreadsheet className="mr-1 h-4 w-4" /> ŞABLON
          </Button>
          <Button data-testid="scan-cards-button" onClick={() => setScanOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700">
            <ScanLine className="mr-1 h-4 w-4" /> KARTVİZİT TARA
          </Button>
          <Button data-testid="ai-sites-button" onClick={() => setAiOpen(true)} className="bg-fuchsia-600 text-white hover:bg-fuchsia-700">
            <Sparkles className="mr-1 h-4 w-4" /> YAPAY ZEKA FİRMA EKLE
          </Button>
          </CollapseBar>
        </div>
      </div></BarTitle>

      {selected.size > 0 && (
        <div data-testid="selection-bar" className="flex flex-nowrap items-center gap-2 overflow-x-auto rounded-xl border border-sky-800/60 bg-sky-950/30 px-3 py-2">
          <span data-testid="selection-count" className="text-xs font-bold text-sky-300">{selected.size} MÜŞTERİ SEÇİLİ</span>
          <Button size="sm" data-testid="fill-info-button" onClick={() => setFillQ(rows.filter((r) => selected.has(r.id)))} className="shrink-0 bg-violet-700 text-white hover:bg-violet-800"><Pencil className="mr-1 h-4 w-4" /> SEÇİLİ {selected.size} FİRMAYI DÜZENLE (YAZI / GÖRSEL)</Button>
          {canCopy && (
            <Button size="sm" data-testid="bulk-copy-firms-button" onClick={() => setCopyOpen(true)} className="shrink-0 bg-sky-700 text-white hover:bg-sky-800"><Share2 className="mr-1 h-4 w-4" /> SEÇİLİ {selected.size} FİRMAYA AKTAR</Button>
          )}
          {!room && (
            <Button size="sm" data-testid="bulk-room-add-button" onClick={bulkToRoom} className="shrink-0 bg-amber-600 text-white hover:bg-amber-700"><Briefcase className="mr-1 h-4 w-4" /> SEÇİLİ {selected.size} ODAYA EKLE</Button>
          )}
          {room && (
            <Button size="sm" data-testid="bulk-room-remove-button" variant="outline" onClick={bulkFromRoom} className="shrink-0 border-amber-700 bg-amber-950/40 text-amber-300"><X className="mr-1 h-4 w-4" /> SEÇİLİ {selected.size} ODADAN ÇIKAR</Button>
          )}
          {allow("can_delete") && (
            <Button size="sm" data-testid="delete-selected-button" variant="outline" onClick={() => setConfirm({ type: "selected" })} className="shrink-0 border-rose-700 bg-rose-950/40 text-rose-300 hover:bg-rose-900/40">
              <Trash2 className="mr-1 h-4 w-4" /> SEÇİLİ {selected.size} SİL
            </Button>
          )}
        </div>
      )}

      {dirtyCount > 0 && (
        <div data-testid="unsaved-banner" className="flex items-center justify-between gap-3 rounded-xl border border-amber-600/50 bg-amber-950/30 p-3 text-sm text-amber-200">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> <b>{dirtyCount}</b> SATIRDA KAYDEDİLMEMİŞ DEĞİŞİKLİK VAR. BAŞKA İŞLEM YAPMADAN KAYDEDİN.</span>
          <Button size="sm" onClick={saveAll} disabled={savingAll} className="h-8 bg-emerald-600 text-white hover:bg-emerald-700"><Save className="mr-1 h-3.5 w-3.5" /> KAYDET</Button>
        </div>
      )}

      <CollapseBar id="report" label="ÜST RAPOR" block>{room ? <TopReport items={roomAll} /> : <TopReport key={reportKey} />}</CollapseBar>

      {room && (
        <CollapseBar id="room-add" label="ODAYA EKLE" block>
        <div className="w-[min(900px,80vw)]">
          <div className="relative">
            <Plus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" />
            <Input data-testid="room-search" value={roomQ} onChange={(e) => searchRoom(e.target.value)} placeholder="ODAYA MÜŞTERİ EKLE — FİRMA / YETKİLİ ARA..." className="h-10 border-slate-700 bg-slate-800 pl-9 text-slate-100" />
          </div>
          {roomRes.length > 0 && (
            <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-800 bg-slate-950">
              {roomRes.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b border-slate-800/60 px-3 py-2 hover:bg-slate-800">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-100">{c.data?.firma || "—"}</div>
                    <div className="truncate text-[11px] text-slate-500">{[c.data?.yetkili, c.data?.il, c.data?.ilce].filter(Boolean).join(" · ")}</div>
                  </div>
                  <Button data-testid={`room-add-${c.id}`} size="sm" onClick={() => addToRoom(c)} className="h-7 shrink-0 bg-blue-600 text-white hover:bg-blue-700"><Plus className="mr-1 h-3.5 w-3.5" /> EKLE</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        </CollapseBar>
      )}

      {/* toolbar */}
      <CollapseBar id={room ? "room-filters" : "customers-filters"} label="FİLTRELER" className="rounded-xl border border-slate-800 bg-slate-900 p-2">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="customer-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="FİRMA, YETKİLİ, EMAIL, GSM ARA..."
            className="h-10 border-slate-700 bg-slate-800 pl-9 text-slate-100"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            data-testid="il-filter"
            value={ilFilter}
            onChange={(e) => { setPage(1); setIlFilter(e.target.value); }}
            className="h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100"
          >
            <option value="">TÜM İLLER</option>
            {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <select data-testid="sube-filter" value={colFilters.subesi || ""} onChange={(e) => setColFilters((f) => ({ ...f, subesi: e.target.value }))} className="h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100">
          <option value="">TÜM ŞUBELER</option>
          {subeler.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button data-testid="hesap-filter-btn" variant="outline" onClick={() => setHesapOnly((v) => !v)} className={`border-slate-700 hover:bg-slate-700 ${hesapOnly ? "bg-emerald-950/40 text-emerald-300" : "bg-slate-800 text-slate-200"}`}>
          <Calculator className="mr-1 h-4 w-4" /> {hesapOnly ? "HESAP DOLU: AÇIK" : "HESAP DOLU"}
        </Button>
        <Button data-testid="eksik-filter-btn" variant="outline" onClick={() => { setEksikOnly((v) => !v); setPage(1); }} className={`border-slate-700 hover:bg-slate-700 ${eksikOnly ? "bg-rose-950/40 text-rose-300" : "bg-slate-800 text-slate-200"}`}>
          <AlertTriangle className="mr-1 h-4 w-4" /> {eksikOnly ? `EKSİK ${MT[mtype]}: AÇIK` : `EKSİK ${MT[mtype]}`} <span data-testid="eksik-count" className="ml-1 rounded bg-rose-800 px-1.5 text-[11px] text-white">{room || missingTotal === null ? eksikCount : missingTotal}</span>
        </Button>
        <select data-testid="eksik-type-select" value={mtype} onChange={(e) => { setMtype(e.target.value); setPage(1); }} className="h-10 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-100">
          {Object.entries(MT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {eksikOnly && viewRows.length > 0 && (
          <Button data-testid="eksik-fill-all-btn" onClick={() => setFillQ(viewRows)} className="bg-violet-700 text-white hover:bg-violet-800"><Pencil className="mr-1 h-4 w-4" /> EKSİKLERİ SIRAYLA DOLDUR ({viewRows.length})</Button>
        )}
        <Button data-testid="ozel-rapor-btn" variant="outline" onClick={() => setReportOpen(true)} className="border-slate-700 bg-slate-800 text-slate-200"><Filter className="mr-1 h-4 w-4" /> ÖZEL RAPOR</Button>
        <select
          data-testid="page-size-select"
          value={pageSize}
          onChange={(e) => { setPage(1); setPageSize(e.target.value === "all" ? "all" : Number(e.target.value)); }}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100"
        >
          <option value={50}>50 SATIR</option>
          <option value={100}>100 SATIR</option>
          <option value={200}>200 SATIR</option>
          <option value="all">TÜMÜ</option>
        </select>
        <Button data-testid="archive-toggle" variant="outline" onClick={() => { setPage(1); setShowArchived((s) => !s); }} className={`border-slate-700 hover:bg-slate-700 ${showArchived ? "bg-emerald-950/40 text-emerald-300" : "bg-slate-800 text-slate-200"}`}>
          <Archive className="mr-1 h-4 w-4" /> {showArchived ? "ARŞİV: AÇIK" : "ARŞİV"}
        </Button>
        <Button data-testid="screen-lock" variant="outline" onClick={toggleLock} className={`border-slate-700 hover:bg-slate-700 ${locked ? "bg-amber-950/40 text-amber-300" : "bg-slate-800 text-slate-200"}`}>
          {locked ? "GÖSTER" : "GİZLE (PIN)"}
        </Button>
      </CollapseBar>

      {pendingReq && (
        <div data-testid="pending-delete-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-700/50 bg-amber-950/30 p-3 text-sm text-amber-200">
          <span>⏳ <b>{pendingReq.count}</b> MÜŞTERİ ÇÖP KUTUSUNDA — ANA ADMİN ONAYI BEKLİYOR. ONAYLANANA KADAR VERİLER GİZLİDİR, 1 GÜN İÇİNDE GERİ ALABİLİRSİNİZ.</span>
          <Button data-testid="cancel-pending-delete" size="sm" onClick={cancelPending} className="h-8 bg-emerald-600 text-white hover:bg-emerald-700">GERİ AL (VERİLERİ GETİR)</Button>
        </div>
      )}

      {/* table */}
      <div ref={scrollRef} className={`overflow-auto rounded-xl border border-slate-800 bg-slate-900 ${locked ? "pointer-events-none select-none blur-md" : ""}`}>
        <table className="mw-table text-sm" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <colgroup>
            <col style={{ width: 44 }} />
            <col style={{ width: 70 }} />
            {localCols.map((col) => <col key={col.key} style={{ width: colW[col.key] || 160 }} />)}
            <col style={{ width: room ? 140 : 110 }} />
          </colgroup>
          <thead className="sticky top-0 z-20">
            <tr className="bg-white text-left text-xs uppercase tracking-wider text-slate-900 [&>th]:bg-white">
              <th className="px-3 pt-3 pb-1">
                <Checkbox data-testid="select-all-checkbox" checked={viewRows.length > 0 && selected.size === viewRows.length} onCheckedChange={toggleAll} />
              </th>
              <th className="px-3 pt-3 pb-1 text-slate-900">S.NO</th>
              {localCols.map((col) => (
                <th
                  key={col.key}
                  draggable={isAdmin}
                  onDragStart={(e) => { if (resizing.current) { e.preventDefault(); return; } setDragKey(col.key); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(col.key)}
                  className={`relative overflow-hidden px-3 pt-3 pb-1 font-bold text-slate-900 ${isAdmin ? "cursor-move" : ""}`}
                  title={isAdmin ? "SÜRÜKLEYEREK TAŞI" : ""}
                >
                  <span onClick={() => toggleSort(col.key)} className="block cursor-pointer select-none truncate pr-2" title="SIRALAMAK İÇİN TIKLA">{col.label}{sortBy.key === col.key ? (sortBy.dir === "asc" ? " ▲" : " ▼") : ""}</span>
                  <span data-testid={`col-resize-${col.key}`} onMouseDown={(e) => startResize(col.key, e)} className="absolute right-0 top-0 z-10 flex h-full w-3 cursor-col-resize items-center justify-center hover:bg-blue-400/80" title="GENİŞLİĞİ AYARLA (SÜRÜKLE)"><span className="h-5 w-[2px] bg-slate-400" /></span>
                </th>
              ))}
              <th className="px-3 pt-3 pb-1 text-right text-slate-900">İŞLEM</th>
            </tr>
            <tr className="border-b-2 border-slate-300 bg-white [&>th]:bg-white">
              <th /><th />
              {localCols.map((col) => (
                <th key={col.key} className="px-3 pb-2 pt-0 align-top">
                  <div className="flex items-center gap-1">
                    <input data-testid={`colfilter-${col.key}`} value={colFilters[col.key] || ""} onChange={(e) => setColFilters((f) => ({ ...f, [col.key]: e.target.value }))} placeholder="filtre" className="h-6 w-full min-w-[40px] rounded border border-slate-300 bg-slate-50 px-1 text-[11px] text-slate-900 placeholder:text-slate-400" />
                    <div className="flex flex-col leading-none">
                      <button data-testid={`sort-asc-${col.key}`} title="A→Z / KÜÇÜKTEN BÜYÜĞE" onClick={() => setSortBy({ key: col.key, dir: "asc" })} className={`px-0.5 text-[12px] leading-none ${sortBy.key === col.key && sortBy.dir === "asc" ? "text-blue-600" : "text-slate-400 hover:text-slate-700"}`}>▲</button>
                      <button data-testid={`sort-desc-${col.key}`} title="Z→A / BÜYÜKTEN KÜÇÜĞE" onClick={() => setSortBy({ key: col.key, dir: "desc" })} className={`px-0.5 text-[12px] leading-none ${sortBy.key === col.key && sortBy.dir === "desc" ? "text-blue-600" : "text-slate-400 hover:text-slate-700"}`}>▼</button>
                    </div>
                  </div>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {viewRows.length === 0 && (
              <tr><td colSpan={localCols.length + 3} className="px-3 py-10 text-center text-slate-500">KAYIT BULUNAMADI</td></tr>
            )}
            {(() => {
              const vItems = rowVirtualizer.getVirtualItems();
              const padTop = vItems.length ? vItems[0].start : 0;
              const padBottom = vItems.length ? rowVirtualizer.getTotalSize() - vItems[vItems.length - 1].end : 0;
              return (<>
                {padTop > 0 && <tr aria-hidden><td colSpan={localCols.length + 3} style={{ height: padTop, padding: 0, border: 0 }} /></tr>}
                {vItems.map((vi) => { const row = viewRows[vi.index]; if (!row) return null; const idx = vi.index; return (
                  <tr key={row.id} data-index={idx} ref={rowVirtualizer.measureElement} data-testid={`customer-row-${row.id}`} className={`border-b border-slate-800/60 hover:bg-slate-800/40 ${dirty[row.id] ? "bg-amber-950/20" : ""}`}>
                    <td className="px-3 py-1">
                      <Checkbox data-testid={`row-checkbox-${row.id}`} checked={selected.has(row.id)} onCheckedChange={() => toggleOne(row.id)} />
                    </td>
                    <td className="px-2 py-1 font-mono text-slate-500">
                      <div className="flex items-center gap-1">
                        <button data-testid={`expand-${row.id}`} onClick={() => setDetailRow(row)} title="DÜZ METİN GÖRÜNÜMÜ (KOPYALA)" className="rounded p-0.5 text-slate-400 hover:bg-slate-700 hover:text-blue-400">
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                        <span>{(pageSize === "all" ? 0 : (page - 1) * pageSize) + idx + 1}</span>
                      </div>
                    </td>
                    {localCols.map((col) => (
                      <td key={col.key} className="whitespace-nowrap px-1 py-0.5 text-slate-200">
                        {col.key === "hesaplama"
                          ? <div className="px-1.5"><button data-testid={`hesapla-${row.id}`} onClick={() => setHesapFor(row)} title="BU MÜŞTERİ İÇİN HESAPLA / YAPIŞTIR" className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0 text-[11px] font-semibold leading-tight ${hasHesap(row) ? "border-emerald-600 bg-emerald-950/50 text-emerald-300 shadow-[0_0_0_1px_rgba(16,185,129,0.4)]" : "border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"}`}><Calculator className="h-3 w-3" />{hasHesap(row) ? "GÖR / GÜNCELLE" : "HESAPLA"}</button></div>
                          : ["il_cirolari", "cikis_il_cirolari", "teklif", "rut"].includes(col.key)
                          ? <div data-testid={`hesaptext-${col.key}-${row.id}`} onClick={() => setDetailRow(row)} title="TIKLA: TÜM BİLGİLERİ DÜZ METİN GÖR" className="min-w-[110px] max-w-[230px] cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap px-1.5 font-mono text-[11px] leading-tight text-slate-300 hover:bg-slate-800/60">{
                              col.key === "rut"
                                ? (row.data.rut ? String(row.data.rut).split(/\s+/).filter(Boolean).join("\n") : "—")
                                : (row.data[col.key] ? String(row.data[col.key]) : "—")
                            }</div>
                          : <div className={`group relative ${fillSet.has(idx + ":" + col.key) ? "rounded ring-2 ring-emerald-400" : ""}`} data-fillcell data-rowidx={idx} data-colkey={col.key}>
                              <InlineCell row={row} col={col} provinces={provinces} onSave={(patch) => editCell(row, patch)} />
                              <span data-testid={`fill-${idx}-${col.key}`} onPointerDown={(e) => startFill(e, idx, col.key)} title="SÜRÜKLE-DOLDUR (EXCEL GİBİ)" className="absolute -bottom-0.5 -right-0.5 z-20 h-2.5 w-2.5 cursor-crosshair rounded-sm border border-white bg-emerald-500 opacity-0 group-hover:opacity-100" />
                            </div>}
                      </td>
                    ))}
                    <td className="px-3 py-1">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`notes-${row.id}`} onClick={() => setNotesFor(row)} title="NOTLAR" className="relative rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-amber-400">
                          <MessageSquare className="h-4 w-4" />
                          {row.note_count > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold text-slate-900">{row.note_count}</span>}
                        </button>
                        <button data-testid={`wa-${row.id}`} onClick={() => setWaFor(row)} title="WHATSAPP MESAJ" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-emerald-400">
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        <button data-testid={`edit-${row.id}`} onClick={() => openEdit(row)} title="KART DÜZENLE" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-blue-400">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid={`archive-${row.id}`} onClick={() => archiveRow(row)} title={showArchived ? "ARŞİVDEN ÇIKAR" : "ARŞİVLE"} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-emerald-400">
                          <Archive className="h-4 w-4" />
                        </button>
                        {room && (
                        <button data-testid={`room-remove-${row.id}`} onClick={() => removeFromRoom(row)} title="ODADAN ÇIKAR" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-amber-400">
                          <X className="h-4 w-4" />
                        </button>
                        )}
                        {allow("can_delete") && (
                        <button data-testid={`delete-${row.id}`} onClick={() => setConfirm({ type: "single", id: row.id })} title="SİL" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-700 hover:text-rose-400">
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ); })}
                {padBottom > 0 && <tr aria-hidden><td colSpan={localCols.length + 3} style={{ height: padBottom, padding: 0, border: 0 }} /></tr>}
              </>);
            })()}
          </tbody>
        </table>
      </div>

      {((pageSize !== "all" && totalPages > 1) || (isSuperAdmin && !pendingReq)) && (
        <div data-testid="customers-footer" className="-mt-2 flex h-7 items-center justify-end gap-4 md:pr-56">
          {isSuperAdmin && !pendingReq && <DeleteAllCustomers onDone={() => { load(); loadPending(); setReportKey((k) => k + 1); }} />}
          {pageSize !== "all" && totalPages > 1 && (
            <div className="flex items-center gap-2">
              <span data-testid="page-info" className="text-xs text-slate-500">SAYFA {page} / {totalPages}</span>
              <Button data-testid="page-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-7 border-slate-700 bg-slate-800 px-3 text-xs text-slate-200">ÖNCEKİ</Button>
              <Button data-testid="page-next" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="h-7 border-slate-700 bg-slate-800 px-3 text-xs text-slate-200">SONRAKİ</Button>
            </div>
          )}
        </div>
      )}

      <CustomerModal open={modalOpen} onOpenChange={(v) => { setModalOpen(v); if (!v) setManualDraft(null); }} columns={columns} initial={editing} onSaved={() => { load(); setReportKey((k) => k + 1); if (manualDraft) { api.delete(`/ai-site/drafts/${manualDraft}`); setManualDraft(null); } }} />
      <NotesModal open={!!notesFor} onOpenChange={(v) => !v && setNotesFor(null)} customer={notesFor} onCountChange={updateNoteCount} />
      <HesaplamaModal row={hesapFor} open={!!hesapFor} onOpenChange={(v) => !v && setHesapFor(null)} onSaved={() => { load(); setReportKey((k) => k + 1); }} />
      <ScanCardsModal open={scanOpen} onOpenChange={setScanOpen} onDone={() => { load(); setReportKey((k) => k + 1); }} />
      <AiSitesModal open={aiOpen} onOpenChange={setAiOpen} onDone={() => { load(); setReportKey((k) => k + 1); }} onManual={(d) => {
        const host = d.site.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
        setAiOpen(false); setManualDraft(d.id);
        setEditing({ data: { firma: host.split(".")[0].toLocaleUpperCase("tr-TR"), web: d.site.replace(/^https?:\/\//i, "").replace(/\/$/, "") } }); setModalOpen(true);
      }} />
      <Dialog open={!!detailRow} onOpenChange={(v) => !v && setDetailRow(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">DÜZ METİN — {detailRow?.data?.firma || "KAYIT"}</DialogTitle></DialogHeader>
          {detailRow && (<>
            <div className="flex justify-end">
              <button data-testid="detail-copy" onClick={() => copyText(plainText(detailRow))} className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs font-semibold text-slate-200 hover:bg-slate-700"><Copy className="h-3.5 w-3.5" /> TÜMÜNÜ KOPYALA</button>
            </div>
            <pre className="select-all whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs leading-relaxed text-slate-200">{plainText(detailRow)}</pre>
          </>)}
        </DialogContent>
      </Dialog>
      <WhatsAppModal row={waFor} open={!!waFor} onOpenChange={(v) => !v && setWaFor(null)} />
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle className="font-head">ÖZEL RAPOR — SERBEST GRUPLA & SÜTUN SEÇ</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">ŞUBE:</span>
              <select data-testid="rapor-sube" value={repSube} onChange={(e) => setRepSube(e.target.value)} className="h-9 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-100">
                <option value="">TÜMÜ</option>{subeler.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">GRUPLA:</span>
              <select data-testid="rapor-grup" value={repGroup} onChange={(e) => setRepGroup(e.target.value)} className="h-9 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-100">
                {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <span className="text-xs text-slate-500">{repData.list.length} MÜŞTERİ</span>
          </div>

          <div className="rounded-lg border border-slate-800">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/60 text-left uppercase text-slate-500"><tr>
                <th className="px-2 py-2">{(columns.find((c) => c.key === repGroup) || {}).label || repGroup}</th>
                <th className="px-2 py-2 text-right">ADET</th>
                {numCols.map((c) => <th key={c.key} className="px-2 py-2 text-right">{c.label}</th>)}
              </tr></thead>
              <tbody>
                {repData.groups.map((g) => (
                  <tr key={g.key} className="border-b border-slate-800/60">
                    <td className="px-2 py-1.5">{g.key}</td>
                    <td className="px-2 py-1.5 text-right">{g.count}</td>
                    {numCols.map((c) => <td key={c.key} className="px-2 py-1.5 text-right font-mono">{formatTRY(g.sums[c.key] || 0)}</td>)}
                  </tr>
                ))}
                <tr className="bg-slate-800/40 font-bold">
                  <td className="px-2 py-1.5">TOPLAM</td><td className="px-2 py-1.5 text-right">{repData.list.length}</td>
                  {numCols.map((c) => <td key={c.key} className="px-2 py-1.5 text-right font-mono">{formatTRY(repData.totals[c.key] || 0)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">DETAY TABLOSUNDA GÖSTERİLECEK SÜTUNLAR (YAN YANA DİZ)</div>
            <div className="flex flex-wrap gap-1.5">
              {columns.map((c) => {
                const on = repCols.includes(c.key);
                return <button key={c.key} data-testid={`repcol-${c.key}`} onClick={() => setRepCols((p) => on ? p.filter((x) => x !== c.key) : [...p, c.key])} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${on ? "bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/40" : "bg-slate-800 text-slate-500"}`}>{c.label}</button>;
              })}
            </div>
          </div>

          <div className="max-h-72 overflow-auto rounded-lg border border-slate-800">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-800/80 text-left uppercase text-slate-500"><tr>
                {repCols.map((k) => <th key={k} className="px-2 py-2">{(columns.find((c) => c.key === k) || {}).label || k}</th>)}
              </tr></thead>
              <tbody>
                {repData.list.map((r) => (
                  <tr key={r.id} className="border-b border-slate-800/60">
                    {repCols.map((k) => {
                      const col = columns.find((c) => c.key === k);
                      const v = r.data[k];
                      return <td key={k} className="px-2 py-1.5 text-slate-200">{col && (col.type === "currency") ? formatTRY(num(v)) : (v || "—")}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-5xl border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle>AKTARMA GEÇMİŞİ</DialogTitle></DialogHeader>
          {logOpen && <CopyLog />}
        </DialogContent>
      </Dialog>
      <FillInfoModal row={fillQ[0] || null} left={fillQ.length} onClose={() => setFillQ((q) => q.slice(1))} onSaved={load} />
      <CopyToFirmsDialog open={copyOpen} onOpenChange={setCopyOpen} ids={[...selected]} onDone={() => { setSelected(new Set()); load(); }} />
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.type === "all" ? "TÜM MÜŞTERİLER SİLİNECEK" : "SİLMEK İSTEDİĞİNİZE EMİN MİSİNİZ?"}
        description={
          confirm?.type === "all"
            ? "SİSTEMDEKİ TÜM MÜŞTERİ KAYITLARI KALICI OLARAK SİLİNECEK. BU İŞLEM GERİ ALINAMAZ."
            : confirm?.type === "selected"
            ? `SEÇİLİ ${selected.size} KAYIT SİLİNECEK.`
            : "BU KAYIT KALICI OLARAK SİLİNECEK."
        }
        onConfirm={doDelete}
      />
    </div>
  );
}
